/**
 * The shape the dashboard endpoints actually send, and how a figure is read
 * (REQ-021).
 *
 * The types here mirror `backend/src/services/dashboard/definitions.ts` and the
 * two routes in `backend/src/routes/staff/dashboard.routes.ts`. They are not a
 * second definition of anything: no formula, no owner and no target value is
 * written down on this side. Both screens are drawn from the payload, which is
 * generated from the registry the computation itself uses, so a definition
 * cannot drift from what a reader sees (AC-001).
 *
 * The one rule this file exists to enforce is the requirement's own: a value
 * that was not measured is never turned into a number. `formatValue` therefore
 * takes an `ok` value and nothing else — the other two statuses have no
 * rendering as a figure at all, and the components render them as words.
 */

import { useI18nStore } from "@/stores/i18nStore";
import { getLocaleString } from "@/utils/dateLocalization";

export type MetricUnit =
  | "eur"
  | "eur_per_month"
  /**
   * The entity books in USD, and the figures taken from settlement and from
   * supplier invoices are in it. Rendering them under a euro sign would be
   * wrong by whatever the rate is that day, so the currency travels with the
   * unit rather than being assumed.
   */
  | "usd"
  | "usd_per_month"
  | "count"
  | "rate"
  | "months"
  | "ratio";

export type MetricValue =
  | { status: "ok"; value: number }
  | { status: "unavailable" }
  | { status: "insufficient_sample" };

/** A MAR-001 §9 target as the definitions endpoint sends it. */
export interface TargetRecord {
  key: string;
  label: string;
  month3: number;
  month6: number;
  month12: number;
  source: string;
  definedOn: string;
  author: string;
}

/** The same target, resolved to the milestone the current plan month falls in. */
export interface TargetComparison {
  key: string;
  label: string;
  planMonth: number;
  milestone: "month3" | "month6" | "month12";
  targetValue: number;
  actual?: number;
  attainment?: number;
  source: string;
  definedOn: string;
  author: string;
}

export interface MetricDescription {
  id: string;
  label: string;
  group: string;
  question: string;
  formula: string;
  source: string;
  owner: string;
  refreshSeconds: number;
  unit: MetricUnit;
  definedOn: string;
  provisionalBelow?: number;
  executive?: number;
  target?: TargetRecord;
}

export interface Freshness {
  stale: boolean;
  /**
   * The source could not say when its data last moved, so `stale: false` is a
   * refusal to judge and not a claim of freshness. Drawn as its own words, for
   * the reason the whole screen exists: an unjudged figure that looks judged is
   * the failure AC-008 is about.
   */
  ageUnknown: boolean;
  sampleRegression: boolean;
}

/**
 * A reading, as `/metrics` sends it. `target` is the comparison rather than
 * the plain record here: the route overwrites the one with the other, and
 * mixing them up would show a month-12 goal in month one.
 */
export interface MetricReading extends Omit<MetricDescription, "target"> {
  value: MetricValue;
  computedAt: string;
  /**
   * When the underlying data last moved, or `null` where the source cannot
   * say. Not the same fact as `computedAt`, which is only when we asked.
   */
  dataAsOf: string | null;
  sampleSize: number;
  provisional: boolean;
  freshness: Freshness;
  target?: TargetComparison;
}

export interface DefinitionsPayload {
  metrics: MetricDescription[];
  targets: TargetRecord[];
  groups: string[];
  executiveLimit: number;
}

/** The source string the backend uses for a metric that has no writer at all. */
export const UNMEASURED_SOURCE = "Not yet measured";

/** The app language's locale; the back office follows it like every page. */
const locale = () => getLocaleString(useI18nStore.getState().language);

const money = (value: number, currency: "EUR" | "USD") =>
  new Intl.NumberFormat(locale(), {
    style: "currency",
    currency,
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  }).format(value);

const eur = (value: number) => money(value, "EUR");
const usd = (value: number) => money(value, "USD");

/**
 * A rounded figure never rounds onto a number the reader takes for a landmark.
 *
 * The stores keep four decimals, and a net paying ratio of 0.996 written as
 * "1.00×" is exactly the figure the cohort screen defines as a cohort that held
 * its count: a decline read as no change, and 0.004 read as nothing at all. So
 * where the usual precision would collapse a value onto one of the landmarks
 * that carry a meaning of their own, another decimal is spent until it does
 * not. A value that really is the landmark is written at the usual precision,
 * because that one is not a rounding.
 */
const MAX_DECIMALS = 6;

function preciseFixed(
  value: number,
  decimals: number,
  landmarks: readonly number[],
): string {
  let places = decimals;
  for (; places < MAX_DECIMALS; places += 1) {
    const rounded = Number(value.toFixed(places));
    if (rounded === value || !landmarks.includes(rounded)) break;
  }
  // Below a millionth of a landmark there is no figure any store here keeps,
  // and no decision that turns on the difference. The digits are chosen above;
  // only the separators follow the language.
  return new Intl.NumberFormat(locale(), {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
    useGrouping: false,
  }).format(value);
}

/** How a measured number is written. Only ever called on an `ok` value. */
export function formatValue(value: number, unit: MetricUnit): string {
  switch (unit) {
    case "eur":
      return eur(value);
    case "eur_per_month":
      return `${eur(value)}/mo`;
    case "usd":
      return usd(value);
    case "usd_per_month":
      return `${usd(value)}/mo`;
    case "rate":
      // Nothing and everything are the two readings a percentage is taken for.
      return `${preciseFixed(value * 100, 1, [0, 100])}%`;
    case "months":
      return `${preciseFixed(value, 1, [0])} months`;
    case "ratio":
      return `${preciseFixed(value, 2, [0, 1])}×`;
    case "count":
    default:
      return new Intl.NumberFormat(locale()).format(value);
  }
}

/**
 * Why a figure is not there, in the words of the definition that describes it.
 *
 * A metric whose source is "Not yet measured" has no writer; anything else
 * reporting unavailable had a writer that could not be reached. The two are
 * different problems with different owners, and a single "no data" would hide
 * which one this is.
 */
export function unavailableReason(metric: { source: string }): string {
  return metric.source === UNMEASURED_SOURCE
    ? "Nothing measures this yet — no source has been built for it."
    : `The source could not be read: ${metric.source}.`;
}

/**
 * The groups, in the order `backend/src/services/dashboard/definitions.ts`
 * declares them.
 *
 * This list is what makes a per-system screen exist: one is drawn for every
 * entry, and every metric carries a group, so a metric added to the registry
 * lands on a screen without anybody remembering to put it there. A group
 * missing from this list would be a set of metrics nobody can open, which is
 * the orphaned-figure failure the requirement is written against — so a test
 * reads the backend registry and fails if it names a group this list does not.
 */
export const METRIC_GROUPS = [
  "money",
  "customers",
  "acquisition",
  "partners",
  "efficiency",
  "product",
  "operations",
] as const;

export type MetricGroup = (typeof METRIC_GROUPS)[number];

/** Typed by the group list, so a new group cannot be added without a name. */
export const GROUP_LABELS: Record<MetricGroup, string> = {
  money: "Money",
  customers: "Customers",
  acquisition: "Acquisition",
  partners: "Partners",
  efficiency: "Efficiency",
  product: "Product",
  operations: "Operations and agents",
};

export const groupLabel = (group: string) =>
  GROUP_LABELS[group as MetricGroup] ?? group;

export const isMetricGroup = (group: string): group is MetricGroup =>
  (METRIC_GROUPS as readonly string[]).includes(group);

/** Where a per-system screen's address is written, once. */
export const groupPath = (group: string) => `/admin/dashboard/system/${group}`;

/** "month 3", from the milestone key MAR-001 §9 is written in. */
export const milestoneLabel = (milestone: string) =>
  milestone.replace(/^month/, "month ");

/** "every 15 minutes", from the seconds the definition records. */
export function refreshLabel(seconds: number): string {
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return hours === 24
      ? "every 24 hours"
      : `every ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const minutes = Math.round(seconds / 60);
  return `every ${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/** The executive figures, in the order their definitions claim (AC-003). */
export function executiveOrder(readings: MetricReading[]): MetricReading[] {
  return readings
    .filter((reading) => reading.executive !== undefined)
    .sort((a, b) => (a.executive ?? 0) - (b.executive ?? 0));
}

/**
 * Every figure in one group, in the order the registry declares them.
 *
 * Deliberately unfiltered: a per-system screen shows the metrics that reach the
 * executive screen alongside the ones that do not, because the ten on the front
 * page are a selection and not the measurement. Nothing here sorts by value —
 * an ordering by size would rank the figures for the reader.
 */
export function groupOrder(
  readings: MetricReading[],
  group: string,
): MetricReading[] {
  return readings.filter((reading) => reading.group === group);
}
